
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Phone, 
  Search, 
  Zap, 
  ShieldAlert, 
  ShieldCheck, 
  Smartphone, 
  Globe, 
  Activity, 
  History,
  Signal,
  AlertTriangle,
  Download
} from 'lucide-react';

export const PhoneView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [number, setNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleSearch = () => {
    if (!number) return;
    setIsSearching(true);
    setTimeout(() => {
      setResult({
        number,
        riskScore: 12,
        status: 'Clean',
        carrier: 'EE Limited (UK)',
        type: 'Mobile',
        isPorted: false,
        simSwapRisk: 'Low',
        location: 'London, UK',
        lastActivity: '2m ago',
        history: [
          { date: '2023-10-28', event: 'Registry Query', source: 'Financial App' },
          { date: '2023-09-12', event: 'Carrier Update', source: 'EE System' }
        ]
      });
      setIsSearching(false);
    }, 1500);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Phone Intelligence</h1>
            <p className="text-sm text-slate-500 font-medium">SIM-Swap detection & carrier forensic analysis.</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-blue-50 text-blue-600 border border-blue-100 rounded-full text-[10px] font-black uppercase tracking-widest">Global SS7 Hook Active</span>
      </div>

      <div className="bg-slate-900 p-8 rounded-[3rem] shadow-2xl relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-white text-lg font-black mb-6 flex items-center">
            <Search size={18} className="mr-2 text-blue-400" /> Identity Signal Scan
          </h2>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input 
                type="text" 
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="+44 7911 123456" 
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-12 py-4 text-white font-mono outline-none focus:ring-4 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <button 
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
            >
              {isSearching ? 'Scanning Nodes...' : 'Verify Number'}
            </button>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-10 -translate-y-1/2 translate-x-1/2"></div>
      </div>

      {result && !isSearching && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 animate-in zoom-in-95 duration-500">
          <div className="md:col-span-4 space-y-6">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm text-center">
              <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                <ShieldCheck size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-900">{result.status}</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Risk Score: {result.riskScore}/100</p>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Forensic Details</h4>
              <div className="space-y-4">
                <DataRow label="Carrier" value={result.carrier} />
                <DataRow label="Line Type" value={result.type} />
                <DataRow label="SIM Swap Risk" value={result.simSwapRisk} valueColor="text-emerald-600" />
                <DataRow label="Ported Status" value={result.isPorted ? 'Yes' : 'No'} />
              </div>
            </div>
          </div>

          <div className="md:col-span-8 bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
             <div className="flex items-center justify-between mb-8">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                  <Activity size={16} className="mr-2 text-blue-600" /> Signal Intelligence
                </h3>
                <button className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline">Download full log</button>
             </div>
             
             <div className="grid grid-cols-2 gap-8 mb-10">
                <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Geolocation</p>
                   <p className="text-xl font-black text-slate-900">{result.location}</p>
                   <div className="mt-3 flex items-center text-[10px] font-black text-blue-600 uppercase">
                      <Globe size={12} className="mr-1" /> Precision: Cell Tower
                   </div>
                </div>
                <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Live Status</p>
                   <div className="flex items-center space-x-2">
                      <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
                      <p className="text-xl font-black text-slate-900">Active / Online</p>
                   </div>
                   <p className="mt-3 text-[10px] font-black text-slate-400 uppercase">Last Signal: {result.lastActivity}</p>
                </div>
             </div>

             <div className="space-y-4">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Activity History</h4>
                <div className="space-y-3">
                  {result.history.map((h: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                       <div className="flex items-center space-x-4">
                          <History size={16} className="text-slate-300" />
                          <span className="text-sm font-bold text-slate-700">{h.event}</span>
                       </div>
                       <div className="text-right">
                          <p className="text-xs font-black text-slate-900">{h.source}</p>
                          <p className="text-[10px] text-slate-400 font-bold">{h.date}</p>
                       </div>
                    </div>
                  ))}
                </div>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DataRow: React.FC<{ label: string, value: string, valueColor?: string }> = ({ label, value, valueColor = 'text-slate-900' }) => (
  <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
    <span className="text-slate-400">{label}</span>
    <span className={valueColor}>{value}</span>
  </div>
);
